import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AssetCard from "@/components/AssetCard";
import useAutoRefresh from "@/lib/useAutoRefresh";
import { ArrowUpRight, Sparkles, Package, Images } from "lucide-react";

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);

  const load = useCallback(() => api.get("/dashboard").then((r) => setData(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  useAutoRefresh(load);

  if (!data) {
    return <div className="text-sm text-muted-foreground animate-pulse">Loading dashboard…</div>;
  }

  return (
    <div className="space-y-14 slide-up" data-testid="dashboard-page">
      {/* Welcome */}
      <section>
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground mb-4">
          {new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" })}
        </div>
        <h1 className="font-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl uppercase font-bold tracking-tight">
          Welcome, {user.name?.split(" ")[0]}.
        </h1>
        <p className="text-muted-foreground mt-3 max-w-2xl">
          The source of truth for every Apollo asset. {data.totals.assets} assets, {data.totals.product_categories} product categories, {data.totals.product_lines} product lines. Always current.
        </p>

        <div className="grid grid-cols-3 gap-2 sm:gap-3 mt-8 max-w-3xl">
          <QuickStat icon={Images} label="Assets" value={data.totals.assets} />
          <QuickStat icon={Package} label="Product Categories" value={data.totals.product_categories} />
          <QuickStat icon={Sparkles} label="Product Lines" value={data.totals.product_lines} />
        </div>
      </section>

      {/* Latest uploads + campaigns */}
      <section className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-3 border border-border rounded-sm bg-surface p-6">
          <SectionHeading eyebrow="Latest" title="Uploads" />
          <div className="mt-4 divide-y divide-border">
            {(data.latest_uploads || []).map((a, i) => {
              const palette = ["strain-sativa", "strain-indica", "strain-hybrid"];
              const chip = palette[i % palette.length];
              return (
                <Link
                  key={a.id}
                  to={`/assets/${a.id}`}
                  state={{ from: "/" }}
                  className="flex items-center gap-4 py-4 first:pt-0 last:pb-0 group"
                  data-testid={`upload-${a.id}`}
                >
                  <div className="w-16 h-16 rounded-sm overflow-hidden bg-muted flex-shrink-0 border border-border">
                    {a.preview_url && <img src={a.preview_url} alt={a.title} className="w-full h-full object-cover group-hover:scale-105 transition duration-500" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1.5">
                      <span className={`px-1.5 py-0.5 rounded-sm text-[9px] font-mono uppercase tracking-[0.2em] ${chip}`}>{a.category}</span>
                      <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{new Date(a.created_at).toLocaleDateString()}</span>
                      <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">· {a.file_type}</span>
                    </div>
                    <h3 className="font-display text-lg uppercase font-semibold truncate">{a.title}</h3>
                    {a.description && <p className="text-sm text-muted-foreground mt-0.5 truncate">{a.description}</p>}
                  </div>
                </Link>
              );
            })}
            {(data.latest_uploads || []).length === 0 && (
              <div className="text-sm text-muted-foreground py-6 text-center">No uploads yet.</div>
            )}
          </div>
        </div>
      </section>

      {/* Featured campaigns */}
      {data.featured_campaigns.length > 0 && (
        <section>
          <SectionHeading eyebrow="Featured" title="Campaigns" />
          <div className="grid md:grid-cols-3 gap-4 mt-6">
            {data.featured_campaigns.map((c) => (
              <div key={c.id} className="group relative aspect-[4/5] rounded-sm overflow-hidden border border-border" data-testid={`campaign-${c.id}`}>
                <img src={c.hero_image} alt={c.name} className="w-full h-full object-cover group-hover:scale-105 transition duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
                <div className="absolute inset-0 p-6 flex flex-col justify-end text-white">
                  <div className="w-6 h-0.5 mb-3" style={{ background: c.color }} />
                  <h3 className="font-display text-3xl uppercase font-bold tracking-tight">{c.name}</h3>
                  <p className="text-sm text-white/80 mt-1">{c.tagline}</p>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Recent assets */}
      <section>
        <div className="flex items-end justify-between">
          <SectionHeading eyebrow="Recently Added" title="Assets" />
          <Link to="/library" className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground hover:text-foreground transition flex items-center gap-1" data-testid="dashboard-see-all-assets">
            Media library <ArrowUpRight className="h-3 w-3" />
          </Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mt-6">
          {data.recent_assets.map((a) => <AssetCard key={a.id} asset={a} />)}
        </div>
      </section>

      {/* Popular downloads */}
      {data.popular_assets.length > 0 && (
        <section>
          <SectionHeading eyebrow="Popular" title="Downloads" />
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mt-6">
            {data.popular_assets.map((a) => <AssetCard key={a.id} asset={a} />)}
          </div>
        </section>
      )}
    </div>
  );
}

function QuickStat({ icon: Icon, label, value }) {
  return (
    <div className="border border-border rounded-sm p-3 sm:p-4 bg-surface" data-testid={`stat-${label.toLowerCase()}`}>
      <div className="flex items-center justify-between gap-1">
        <span className="font-mono text-[9px] sm:text-[10px] uppercase tracking-[0.2em] sm:tracking-[0.3em] text-muted-foreground leading-tight">{label}</span>
        <Icon className="h-3 w-3 sm:h-3.5 sm:w-3.5 text-muted-foreground flex-shrink-0" />
      </div>
      <div className="font-display text-2xl sm:text-3xl font-bold mt-2">{value}</div>
    </div>
  );
}

function SectionHeading({ eyebrow, title }) {
  return (
    <div>
      <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground mb-1">{eyebrow}</div>
      <h2 className="font-display text-2xl md:text-3xl uppercase font-semibold tracking-tight">{title}</h2>
    </div>
  );
}
